import * as React from "react";
import { TaskViewVisualStore } from "../stores";
import { observer } from "mobx-react-lite";
import {
    DatePicker,
    Form,
    Input,
    Select,
    TimePicker,
    Upload,
    message,
} from "antd";
import {
    ActionField,
    DateEditorConfig,
} from "../../administration/types/Actions";
import TextArea from "antd/lib/input/TextArea";
import { UploadChangeParam } from "antd/lib/upload";
import {
    FULL_DATE,
    ONLY_DATE,
    ONLY_TIME,
    Utils,
} from "../../common/misc/Utils";
import { Dayjs } from "dayjs";
import { endpoints } from "../../../config";
import { MetadataDefinition } from "../../administration/types/Metadata";
import { getActionMetadataDefinition } from "./ActionMetadataSelection";
import { authenticatedUploadRequest } from "./authenticatedUploadRequest";
type Props = {
    actionId: string;
    store: TaskViewVisualStore;
};

const Dragger = Upload.Dragger;

export const getMultiValueMetadataOptions = (
    value: unknown,
    definition: MetadataDefinition,
) => {
    const values = Array.isArray(value)
        ? value
        : value === null || value === undefined
          ? []
          : [value];
    return values.map((item) => ({
        value: item,
        label: String(
            Utils.getMetadataParseValue(item, {
                ...definition,
                isMultiValue: false,
            }) ?? "",
        ),
    }));
};

const ActionInputForm: React.FC<Props> = ({ actionId, store }) => {
    const [form] = Form.useForm();
    React.useEffect(() => {
        if (!store.selectedAction) {
            form.resetFields();
        }
    }, [store.selectedAction, form]);

    const action = store.availableActions.find((x) => x.id === actionId)!;

    const fields =
        action.fields.filter(
            (field) =>
                field.fieldType === "UserInput" ||
                (field.fieldType === "Metadata" &&
                    getActionMetadataDefinition(
                        field,
                        store.metadataDefinitions,
                    )?.isMultiValue),
        ) || [];

    const onChange = (info: UploadChangeParam) => {
        const status = info.file.status;
        if (status !== "uploading") {
            console.log(info.file, info.fileList);
        }

        if (status === "done") {
            message.success(`${info.file.name} file uploaded successfully.`);
        } else if (status === "error") {
            message.error(`${info.file.name} file upload failed.`);
        }
    };

    const handleSubmit = () => {
        form.validateFields()
            .then(async (values) => {
                store.setIsActionLoading(true);
                const payload: { [key: string]: unknown } = {};
                Object.keys(values).forEach((k) => {
                    const field = fields.find((x) => x.name === k);
                    payload[k] =
                        field?.editor?.$type === "FileEditorConfig"
                            ? values[k]?.fileList?.flatMap(
                                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                  (x: any) => x?.response,
                              ) || []
                            : values[k];
                    if (field?.editor?.$type === "DateEditorConfig") {
                        const editor = field.editor as DateEditorConfig;
                        if (editor.dateType === ONLY_DATE) {
                            payload[k] = Utils.removeLocalTimeZone(
                                payload[k] as Dayjs,
                            );
                        } else if (editor.dateType === FULL_DATE) {
                            payload[k] = Utils.ignoreLocalTimeZone(
                                payload[k] as Dayjs,
                            );
                        }
                    }
                });

                await store
                    .executeAction(
                        store.taskPreview!.id,
                        actionId,
                        payload,
                        action.isClientSide,
                    )
                    .then(() => {
                        store.setSelectedAction(undefined, undefined);
                    })
                    .catch((err) => {
                        message.error(
                            "An error occured during execution of action.",
                        );
                        console.log(err);
                    })
                    .finally(() => {
                        store.setIsActionLoading(false);
                    });
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const getProjectId = () => {
        return store.taskPreview!.projectId;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const getInput = (config?: any) => {
        const editorType = config?.$type;
        switch (editorType) {
            case "SelectEditorConfig": {
                const props = {
                    options: config.items.map((x: string) => ({
                        value: x,
                        label: x,
                    })),
                };

                if (config.allowMultiple) {
                    Object.assign(props, { mode: "multiple" });
                }

                return <Select {...props} />;
            }
            case "TextEditorConfig": {
                const props = config.maxLength
                    ? { maxLength: config.maxLength }
                    : {};

                if (config.isMultiLine) {
                    return <TextArea {...props} />;
                } else {
                    return <Input {...props} />;
                }
            }
            case "FileEditorConfig": {
                const props = {
                    multiple: config.allowMultiple || false,
                    maxCount: config.allowMultiple ? 5 : 1,
                };

                if (config.extension) {
                    Object.assign(props, { accept: config.extension });
                }

                return (
                    <Dragger
                        className="upload-dragger"
                        name="files"
                        onChange={onChange}
                        customRequest={authenticatedUploadRequest}
                        action={`${endpoints.tasksUrl}file-upload/multiple/project/${getProjectId()}`}
                        {...props}
                    >
                        <p className="ant-upload-hint">
                            <i
                                className="alpha-icon md upload"
                                style={{ verticalAlign: "middle" }}
                            />
                            Drag and drop file here to upload...
                        </p>
                    </Dragger>
                );
            }
            case "DateEditorConfig": {
                return config.dateType === ONLY_TIME ? (
                    <TimePicker style={{ width: "100%" }} />
                ) : (
                    <DatePicker
                        format={Utils.getDateFormat(
                            config.dateType === FULL_DATE,
                        )}
                        style={{ width: "100%" }}
                        showTime={config.dateType === FULL_DATE}
                    />
                );
            }
            default: {
                return <Input />;
            }
        }
    };

    const getItem = (field: ActionField) => {
        const metadataDefinition =
            field.fieldType === "Metadata"
                ? getActionMetadataDefinition(field, store.metadataDefinitions)
                : undefined;
        const metadataValue = metadataDefinition
            ? store.taskPreview?.metadata?.project?.[metadataDefinition.name]
            : undefined;
        const metadataOptions = metadataDefinition
            ? getMultiValueMetadataOptions(metadataValue, metadataDefinition)
            : [];
        const input = metadataDefinition ? (
            <Select
                options={metadataOptions}
                placeholder={
                    metadataOptions.length === 0
                        ? "No values are available"
                        : "Select a value"
                }
                disabled={metadataOptions.length === 0}
            />
        ) : (
            getInput(field.editor)
        );

        return (
            <Form.Item
                className="dialog-field"
                colon={false}
                label={
                    <span className="dialog-field-label">
                        {field.title ?? field.name}
                    </span>
                }
                name={field.name}
                key={field.name}
                rules={
                    field.isRequired
                        ? [
                              {
                                  required: true,
                                  message: `${field.title ?? field.name} field is required`,
                              },
                          ]
                        : []
                }
                initialValue={
                    field.fieldType === "UserInput"
                        ? field.defaultValue
                        : undefined
                }
            >
                {input}
            </Form.Item>
        );
    };
    return (
        <Form
            form={form}
            onFinish={handleSubmit}
            className="alpha-form"
            layout="vertical"
            id="execute-action-form"
        >
            {fields.map((x) => getItem(x))}
        </Form>
    );
};

export default observer(ActionInputForm);
